import { supabase } from '../lib/supabase';
import { GitHubAnalysisService } from './githubAnalysisService';
import { logger } from './loggingService';

export interface PlanTreeNode {
  id: string;
  name: string;
  type: 'phase' | 'step' | 'template' | 'section';
  status: 'complete' | 'partial' | 'incomplete';
  completionPercentage?: number;
  description?: string;
  content?: any;
  children?: PlanTreeNode[];
  icon?: React.ReactNode;
  warnings?: string[];
  missingItems?: string[];
  metadata?: Record<string, any>;
}

export interface PlanTreeData {
  tree: PlanTreeNode[];
  overallCompletion: number;
  lastUpdated: string;
}

export class PlanTreeService {
  /**
   * Generate comprehensive plan tree from GitHub analysis and blueprint data
   */
  static async generatePlanTree(
    projectId: string,
    userId: string
  ): Promise<PlanTreeData> {
    try {
      // Get agent analysis results if available (preferred over basic GitHub analysis)
      const { data: projectAnalysis } = await supabase
        .from('project_analysis')
        .select('analysis_metadata')
        .eq('project_id', projectId)
        .maybeSingle();

      let agentAnalysisResults = null;
      if (projectAnalysis?.analysis_metadata?.agent_analysis) {
        agentAnalysisResults = projectAnalysis.analysis_metadata.agent_analysis;
        logger.debug('Using agent analysis results for plan tree', { projectId });
      }

      // Get GitHub analysis (fallback or supplement)
      const analysis = await GitHubAnalysisService.analyzeRepository(
        projectId,
        userId
      );

      // Get blueprint data
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('project_id', projectId)
        .maybeSingle();

      // Get project details
      const { data: project } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .maybeSingle();

      if (!project) {
        const { logger } = await import('./loggingService');
        logger.warn('Project not found for plan tree generation', { projectId });
        return this.getDefaultPlanTree();
      }

      if (!analysis && !agentAnalysisResults) {
        return this.getDefaultPlanTree();
      }

      // Build dynamic tree structure
      const tree: PlanTreeNode[] = [
        {
          id: 'sdlc-phases',
          name: 'SDLC Phases',
          type: 'phase',
          status: 'partial',
          children: [
            await this.generateRequirementsPhase(
              projectId,
              analysis,
              blueprint,
              agentAnalysisResults
            ),
            await this.generateDesignPhase(projectId, analysis, blueprint, agentAnalysisResults),
            await this.generateDevelopmentPhase(projectId, analysis, blueprint, agentAnalysisResults),
            await this.generateTestingPhase(projectId, analysis, blueprint, agentAnalysisResults),
            await this.generateDeploymentPhase(projectId, analysis, blueprint, agentAnalysisResults),
            await this.generateMaintenancePhase(projectId, analysis, blueprint, agentAnalysisResults),
          ],
        },
      ];

      const overallCompletion = this.calculateOverallCompletion(tree);

      return {
        tree,
        overallCompletion,
        lastUpdated: new Date().toISOString(),
      };
    } catch (error) {
      logger.error('Error generating plan tree:', error);
      return this.getDefaultPlanTree();
    }
  }

  /**
   * Phase 1: Requirements & Analysis
   */
  private static async generateRequirementsPhase(
    projectId: string,
    analysis: any,
    blueprint: any,
    agentAnalysis?: any
  ): Promise<PlanTreeNode> {
    const { data: features } = await supabase
      .from('features')
      .select('*')
      .eq('project_id', projectId);

    // Use agent analysis if available, otherwise fall back to blueprint/existence checks
    const agentPhaseData = agentAnalysis?.phases?.find((p: any) => p.phaseName === 'requirements');
    const agentCompleteness = agentPhaseData?.mergedAnalysis?.overallCompleteness;

    const hasScope = !!blueprint?.content?.scope;
    const hasFeatures = (features?.length || 0) > 0;
    const hasUserStories = !!blueprint?.content?.user_stories;
    const hasStakeholders = !!blueprint?.content?.stakeholders;

    // Use agent analysis completeness if available, otherwise calculate from existence
    const completionPercentage = agentCompleteness !== undefined
      ? agentCompleteness
      : this.calculatePercentage([
          hasScope,
          hasFeatures,
          hasUserStories,
          hasStakeholders,
        ]);

    return {
      id: 'requirements',
      name: '1. Requirements & Analysis',
      type: 'phase',
      status: this.calculatePhaseStatus([
        hasScope,
        hasFeatures,
        hasUserStories,
        hasStakeholders,
        completionPercentage >= 50, // Consider partial if agent says 50%+
      ]),
      completionPercentage,
      children: [
        {
          id: 'scope',
          name: 'Scope Definition',
          type: 'step',
          status: hasScope ? 'complete' : 'incomplete',
          description: 'Define project scope and boundaries',
          content: blueprint?.content?.scope || agentPhaseData?.mergedAnalysis?.combinedFindings?.slice(0, 5) || null,
        },
        {
          id: 'features',
          name: 'Features & Requirements',
          type: 'step',
          status: hasFeatures ? 'complete' : 'incomplete',
          description: 'Define features and atomic requirements',
          content: { features: features || [] },
        },
        {
          id: 'user-stories',
          name: 'User Stories',
          type: 'step',
          status: hasUserStories ? 'complete' : 'incomplete',
          description: 'Create user stories with acceptance criteria',
          content: blueprint?.content?.user_stories || null,
        },
        {
          id: 'stakeholders',
          name: 'Stakeholder Analysis',
          type: 'step',
          status: hasStakeholders ? 'complete' : 'incomplete',
          description: 'Identify and analyze stakeholders',
          content: blueprint?.content?.stakeholders || null,
        },
      ],
    };
  }

  /**
   * Phase 2: Design & Architecture
   */
  private static async generateDesignPhase(
    projectId: string,
    analysis: any,
    blueprint: any,
    agentAnalysis?: any
  ): Promise<PlanTreeNode> {
    const { data: architectureNodes } = await supabase
      .from('architecture_nodes')
      .select('*')
      .eq('project_id', projectId);

    const { data: schemaTables } = await supabase
      .from('schema_tables')
      .select('*')
      .eq('project_id', projectId);

    // Use agent analysis if available
    const agentPhaseData = agentAnalysis?.phases?.find((p: any) => p.phaseName === 'design');
    const agentCompleteness = agentPhaseData?.mergedAnalysis?.overallCompleteness;

    const hasArchitecture = (architectureNodes?.length || 0) > 0;
    const hasTechStack = !!analysis?.techStack;
    const hasSchema = (schemaTables?.length || 0) > 0;
    const hasAPIDesign = !!blueprint?.content?.api_design;
    const hasWireframes = !!blueprint?.content?.wireframes;
    const hasTheme = !!blueprint?.content?.theme;

    const completionPercentage = agentCompleteness !== undefined
      ? agentCompleteness
      : this.calculatePercentage([
          hasArchitecture,
          hasTechStack,
          hasSchema,
          hasAPIDesign,
          hasWireframes,
          hasTheme,
        ]);

    return {
      id: 'design',
      name: '2. Design & Architecture',
      type: 'phase',
      status: this.calculatePhaseStatus([
        hasArchitecture,
        hasTechStack,
        hasSchema,
        hasAPIDesign,
        hasWireframes,
        hasTheme,
        completionPercentage >= 50,
      ]),
      completionPercentage,
      children: [
        {
          id: 'architecture',
          name: 'Architecture Design',
          type: 'step',
          status: hasArchitecture ? 'complete' : 'incomplete',
          description: 'Design overall system architecture',
          content: {
            nodes: architectureNodes || [],
            patterns: analysis?.architecture?.designPatterns || [],
          },
        },
        {
          id: 'tech-stack',
          name: 'Technology Stack Selection',
          type: 'step',
          status: hasTechStack ? 'complete' : 'incomplete',
          description: 'Choose appropriate technologies',
          content: analysis?.techStack || null,
        },
        {
          id: 'schema',
          name: 'Database Schema Design',
          type: 'step',
          status: hasSchema ? 'complete' : 'incomplete',
          description: 'Design database tables and relationships',
          content: { tables: schemaTables || [] },
        },
        {
          id: 'api-design',
          name: 'API Design',
          type: 'step',
          status: hasAPIDesign ? 'complete' : 'incomplete',
          description: 'Design RESTful or GraphQL APIs',
          content: blueprint?.content?.api_design || null,
        },
        {
          id: 'wireframes',
          name: 'Wireframes & Mockups',
          type: 'step',
          status: hasWireframes ? 'complete' : 'incomplete',
          description: 'Create visual representations of UI',
          content: blueprint?.content?.wireframes || null,
        },
        {
          id: 'theme',
          name: 'Theme & Branding',
          type: 'step',
          status: hasTheme ? 'complete' : 'incomplete',
          description: 'Define visual theme and branding guidelines',
          content: blueprint?.content?.theme || null,
        },
      ],
    };
  }

  /**
   * Phase 3: Development & Implementation
   */
  private static async generateDevelopmentPhase(
    projectId: string,
    analysis: any,
    blueprint: any,
    agentAnalysis?: any
  ): Promise<PlanTreeNode> {
    const { data: standards } = await supabase
      .from('standards')
      .select('*')
      .eq('project_id', projectId);

    // Use agent analysis if available
    const agentPhaseData = agentAnalysis?.phases?.find((p: any) => p.phaseName === 'development');
    const agentCompleteness = agentPhaseData?.mergedAnalysis?.overallCompleteness;

    const hasCodingStandards = (analysis?.standards?.length || 0) > 0;
    const hasFeatureDev = !!blueprint?.content?.development_plan;
    const hasUnitTesting =
      (analysis?.techStack?.testing?.frameworks?.length || 0) > 0;
    const hasIntegrationTesting = !!blueprint?.content?.integration_testing;

    const completionPercentage = agentCompleteness !== undefined
      ? agentCompleteness
      : this.calculatePercentage([
          hasCodingStandards,
          hasFeatureDev,
          hasUnitTesting,
          hasIntegrationTesting,
        ]);

    return {
      id: 'development',
      name: '3. Development & Implementation',
      type: 'phase',
      status: this.calculatePhaseStatus([
        hasCodingStandards,
        hasFeatureDev,
        hasUnitTesting,
        hasIntegrationTesting,
        completionPercentage >= 50,
      ]),
      completionPercentage,
      children: [
        {
          id: 'coding-standards',
          name: 'Coding Standards & Conventions',
          type: 'step',
          status: hasCodingStandards ? 'complete' : 'incomplete',
          description: 'Establish coding guidelines',
          content: {
            standards: analysis?.standards || [],
            custom: standards || [],
          },
        },
        {
          id: 'feature-dev',
          name: 'Feature Development',
          type: 'step',
          status: hasFeatureDev ? 'partial' : 'incomplete',
          description: 'Implement features based on requirements',
          content: blueprint?.content?.development_plan || null,
        },
        {
          id: 'unit-testing',
          name: 'Unit Testing',
          type: 'step',
          status: hasUnitTesting ? 'partial' : 'incomplete',
          description: 'Write unit tests for code components',
          content: {
            frameworks: analysis?.techStack?.testing?.frameworks || [],
          },
        },
        {
          id: 'integration-testing',
          name: 'Integration Testing',
          type: 'step',
          status: hasIntegrationTesting ? 'complete' : 'incomplete',
          description: 'Test interactions between modules',
          content: blueprint?.content?.integration_testing || null,
        },
      ],
    };
  }

  /**
   * Phase 4: Testing & Quality Assurance
   */
  private static async generateTestingPhase(
    projectId: string,
    analysis: any,
    blueprint: any,
    agentAnalysis?: any
  ): Promise<PlanTreeNode> {
    // Use agent analysis if available
    const agentPhaseData = agentAnalysis?.phases?.find((p: any) => p.phaseName === 'testing');
    const agentCompleteness = agentPhaseData?.mergedAnalysis?.overallCompleteness;

    const hasQAAudit = !!blueprint?.content?.qa_audit;
    const hasSecurityTesting = !!blueprint?.content?.security_testing;
    const hasPerformanceTesting = !!blueprint?.content?.performance_testing;
    const hasUAT = !!blueprint?.content?.user_acceptance_testing;

    const completionPercentage = agentCompleteness !== undefined
      ? agentCompleteness
      : this.calculatePercentage([
          hasQAAudit,
          hasSecurityTesting,
          hasPerformanceTesting,
          hasUAT,
        ]);

    return {
      id: 'testing',
      name: '4. Testing & Quality Assurance',
      type: 'phase',
      status: this.calculatePhaseStatus([
        hasQAAudit,
        hasSecurityTesting,
        hasPerformanceTesting,
        hasUAT,
        completionPercentage >= 50,
      ]),
      completionPercentage,
      children: [
        {
          id: 'qa-audit',
          name: 'QA Audit & Review',
          type: 'step',
          status: hasQAAudit ? 'complete' : 'incomplete',
          description: 'Perform comprehensive quality assurance',
          content: blueprint?.content?.qa_audit || null,
        },
        {
          id: 'security-testing',
          name: 'Security Testing',
          type: 'step',
          status: hasSecurityTesting ? 'complete' : 'incomplete',
          description: 'Identify and mitigate security vulnerabilities',
          content: blueprint?.content?.security_testing || null,
        },
        {
          id: 'performance-testing',
          name: 'Performance Testing',
          type: 'step',
          status: hasPerformanceTesting ? 'complete' : 'incomplete',
          description: 'Evaluate system performance under load',
          content: blueprint?.content?.performance_testing || null,
        },
        {
          id: 'user-acceptance-testing',
          name: 'User Acceptance Testing (UAT)',
          type: 'step',
          status: hasUAT ? 'complete' : 'incomplete',
          description: 'Validate against user requirements',
          content: blueprint?.content?.user_acceptance_testing || null,
        },
      ],
    };
  }

  /**
   * Phase 5: Deployment & Operations
   */
  private static async generateDeploymentPhase(
    projectId: string,
    analysis: any,
    blueprint: any,
    agentAnalysis?: any
  ): Promise<PlanTreeNode> {
    // Use agent analysis if available
    const agentPhaseData = agentAnalysis?.phases?.find((p: any) => p.phaseName === 'deployment');
    const agentCompleteness = agentPhaseData?.mergedAnalysis?.overallCompleteness;

    const hasCICD = !!blueprint?.content?.cicd_pipeline;
    const hasMonitoring = !!blueprint?.content?.monitoring;
    const hasReleaseManagement = !!blueprint?.content?.release_management;

    const completionPercentage = agentCompleteness !== undefined
      ? agentCompleteness
      : this.calculatePercentage([
          hasCICD,
          hasMonitoring,
          hasReleaseManagement,
        ]);

    return {
      id: 'deployment',
      name: '5. Deployment & Operations',
      type: 'phase',
      status: this.calculatePhaseStatus([
        hasCICD,
        hasMonitoring,
        hasReleaseManagement,
        completionPercentage >= 50,
      ]),
      completionPercentage,
      children: [
        {
          id: 'ci-cd',
          name: 'CI/CD Pipeline Setup',
          type: 'step',
          status: hasCICD ? 'complete' : 'incomplete',
          description: 'Automate build, test, and deployment',
          content: blueprint?.content?.cicd_pipeline || null,
        },
        {
          id: 'monitoring',
          name: 'Monitoring & Logging',
          type: 'step',
          status: hasMonitoring ? 'complete' : 'incomplete',
          description: 'Implement system monitoring and logging',
          content: blueprint?.content?.monitoring || null,
        },
        {
          id: 'release-management',
          name: 'Release Management',
          type: 'step',
          status: hasReleaseManagement ? 'complete' : 'incomplete',
          description: 'Plan and execute software releases',
          content: blueprint?.content?.release_management || null,
        },
      ],
    };
  }

  /**
   * Phase 6: Maintenance & Evolution
   */
  private static async generateMaintenancePhase(
    projectId: string,
    analysis: any,
    blueprint: any,
    agentAnalysis?: any
  ): Promise<PlanTreeNode> {
    // Use agent analysis if available
    const agentPhaseData = agentAnalysis?.phases?.find((p: any) => p.phaseName === 'maintenance');
    const agentCompleteness = agentPhaseData?.mergedAnalysis?.overallCompleteness;

    const hasBugFixing = !!blueprint?.content?.bug_fixing;
    const hasEnhancements = !!blueprint?.content?.enhancements;
    const hasDocumentationUpdates = !!blueprint?.content?.documentation_updates;

    const completionPercentage = agentCompleteness !== undefined
      ? agentCompleteness
      : this.calculatePercentage([
          hasBugFixing,
          hasEnhancements,
          hasDocumentationUpdates,
        ]);

    return {
      id: 'maintenance',
      name: '6. Maintenance & Evolution',
      type: 'phase',
      status: this.calculatePhaseStatus([
        hasBugFixing,
        hasEnhancements,
        hasDocumentationUpdates,
        completionPercentage >= 50,
      ]),
      completionPercentage,
      children: [
        {
          id: 'bug-fixing',
          name: 'Bug Fixing & Support',
          type: 'step',
          status: hasBugFixing ? 'complete' : 'incomplete',
          description: 'Address issues and provide user support',
          content: blueprint?.content?.bug_fixing || null,
        },
        {
          id: 'enhancements',
          name: 'Feature Enhancements',
          type: 'step',
          status: hasEnhancements ? 'complete' : 'incomplete',
          description: 'Implement new features and improvements',
          content: blueprint?.content?.enhancements || null,
        },
        {
          id: 'documentation-updates',
          name: 'Documentation Updates',
          type: 'step',
          status: hasDocumentationUpdates ? 'complete' : 'incomplete',
          description: 'Keep documentation current',
          content: blueprint?.content?.documentation_updates || null,
        },
      ],
    };
  }

  /**
   * Calculate phase status based on child statuses
   */
  private static calculatePhaseStatus(
    completionFlags: boolean[]
  ): 'complete' | 'partial' | 'incomplete' {
    const total = completionFlags.length;
    const completed = completionFlags.filter(f => f).length;

    if (completed === total) return 'complete';
    if (completed > 0) return 'partial';
    return 'incomplete';
  }

  /**
   * Calculate completion percentage
   */
  private static calculatePercentage(completionFlags: boolean[]): number {
    const total = completionFlags.length;
    const completed = completionFlags.filter(f => f).length;
    return Math.round((completed / total) * 100);
  }

  /**
   * Calculate overall completion across all phases
   */
  private static calculateOverallCompletion(tree: PlanTreeNode[]): number {
    let totalSteps = 0;
    let completedSteps = 0;

    const countSteps = (node: PlanTreeNode) => {
      if (node.type === 'step') {
        totalSteps++;
        if (node.status === 'complete') completedSteps++;
      }
      if (node.children) {
        node.children.forEach(countSteps);
      }
    };

    tree.forEach(countSteps);

    return totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  }

  /**
   * Get default/fallback plan tree
   */
  private static getDefaultPlanTree(): PlanTreeData {
    return {
      tree: [],
      overallCompletion: 0,
      lastUpdated: new Date().toISOString(),
    };
  }

  /**
   * Update plan section content
   */
  static async updatePlanSection(
    projectId: string,
    sectionId: string,
    content: any
  ): Promise<boolean> {
    try {
      // Update blueprint content with new section data
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('content')
        .eq('project_id', projectId)
        .single();

      if (!blueprint) return false;

      const updatedContent = {
        ...blueprint.content,
        [sectionId]: content,
      };

      const { error } = await supabase
        .from('project_blueprints')
        .update({
          content: updatedContent,
          updated_at: new Date().toISOString(),
        })
        .eq('project_id', projectId);

      return !error;
    } catch (error) {
      logger.error('Error updating plan section:', error);
      return false;
    }
  }
}
